import { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Shield, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const PERM_FIELDS = [
  { key: 'perm_place_orders',  label: 'Place Orders',     hint: 'Can add to cart and submit orders' },
  { key: 'perm_view_orders',   label: 'View Orders',      hint: 'Can see orders list and detail' },
  { key: 'perm_view_pricing',  label: 'View Pricing',     hint: 'Can see price per M and order totals' },
  { key: 'perm_approve_proof', label: 'Approve Proofs',   hint: 'Can approve or reject proof files' },
  { key: 'perm_upload_sample', label: 'Upload Samples',   hint: 'Can upload sample files on line items' },
  { key: 'perm_manage_users',  label: 'Manage Users',     hint: 'Can access the Users admin panel' },
];

const SCOPE_OPTIONS = [
  { value: 'own',      label: 'Own Orders Only',      hint: "Can only see orders they placed" },
  { value: 'customer', label: 'All Customer Orders',   hint: "Can see all orders for their customer" },
];

const EMPTY_ROLE = {
  name: '', slug: '',
  perm_place_orders: 1, perm_view_orders: 1, perm_view_pricing: 1,
  perm_approve_proof: 0, perm_upload_sample: 1, perm_manage_users: 0,
  order_scope: 'customer', is_admin_role: 0,
};

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function RoleCard({ role, customerId, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const activePerms = PERM_FIELDS.filter(p => role[p.key]);

  return (
    <div className="card mb-16" style={{ padding: 0 }}>
      <div
        style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <Shield size={16} style={{ color: role.is_admin_role ? 'var(--gold)' : 'var(--gray-500)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="fw-600">{role.name}</div>
          <div className="text-sm text-muted">
            {activePerms.length} permissions · Scope: {SCOPE_OPTIONS.find(s => s.value === role.order_scope)?.label}
            {role.is_admin_role ? ' · Admin Role' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); onEdit(role); }}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); onDelete(role); }}>
            <Trash2 size={13} />
          </button>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--gray-100)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
            {PERM_FIELDS.map(p => (
              <div key={p.key} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', borderRadius: 6,
                background: role[p.key] ? 'var(--green-bg)' : 'var(--gray-100)',
                border: `1px solid ${role[p.key] ? '#b7e4c7' : 'var(--gray-200)'}`,
              }}>
                <span style={{ fontSize: 14 }}>{role[p.key] ? '✓' : '✗'}</span>
                <div>
                  <div className="text-sm fw-600" style={{ color: role[p.key] ? 'var(--green)' : 'var(--gray-500)' }}>{p.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{p.hint}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            <span className="badge" style={{ background: 'var(--blue-bg)', color: '#1565c0' }}>
              Order Scope: {SCOPE_OPTIONS.find(s => s.value === role.order_scope)?.label}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleModal({ role, customerId, customerSlug, onClose, onSaved }) {
  const isEdit = !!role?.id;
  const [form, setForm] = useState(role ? { ...role } : { ...EMPTY_ROLE });
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(!isEdit);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'name' && autoSlug) next.slug = `${customerSlug}-${slugify(v)}`;
    return next;
  });

  const save = async () => {
    if (!form.name) return toast.error('Name is required');
    if (!form.slug) return toast.error('Slug is required');
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/api/roles/customers/${customerId}/roles/${form.id}`, form);
      } else {
        await api.post(`/api/roles/customers/${customerId}/roles`, form);
      }
      toast.success(`Role ${isEdit ? 'updated' : 'created'}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Role' : 'New Role'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Role Name <span className="required">*</span></label>
            <input className="form-input" value={form.name}
              onChange={e => set('name', e.target.value)} placeholder="e.g. Ordering Clerk" />
          </div>
          <div className="form-group">
            <label className="form-label">Slug</label>
            <input className="form-input" value={form.slug}
              onChange={e => { setAutoSlug(false); set('slug', e.target.value); }}
              placeholder="auto-generated" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Order Data Scope</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {SCOPE_OPTIONS.map(s => (
              <label key={s.value} style={{
                flex: 1, display: 'flex', gap: 10, padding: '10px 12px',
                border: `2px solid ${form.order_scope === s.value ? 'var(--navy-light)' : 'var(--gray-300)'}`,
                borderRadius: 8, cursor: 'pointer',
                background: form.order_scope === s.value ? 'var(--blue-bg)' : 'var(--white)',
              }}>
                <input type="radio" name="scope" value={s.value} checked={form.order_scope === s.value}
                  onChange={() => set('order_scope', s.value)} style={{ marginTop: 2 }} />
                <div>
                  <div className="text-sm fw-600">{s.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{s.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Permissions</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {PERM_FIELDS.map(p => (
              <label key={p.key} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
                border: `1px solid ${form[p.key] ? '#b7e4c7' : 'var(--gray-200)'}`,
                borderRadius: 6, cursor: 'pointer',
                background: form[p.key] ? 'var(--green-bg)' : 'var(--gray-50)',
              }}>
                <input type="checkbox" checked={!!form[p.key]}
                  onChange={e => set(p.key, e.target.checked ? 1 : 0)} style={{ marginTop: 3 }} />
                <div>
                  <div className="text-sm fw-600">{p.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{p.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.is_admin_role}
              onChange={e => set('is_admin_role', e.target.checked ? 1 : 0)} />
            <span className="text-sm fw-600">Mark as Admin Role</span>
          </label>
          <div className="form-hint">Admin roles are shown first and get a gold indicator.</div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RoleManagerPage() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [modal, setModal] = useState(null); // null | 'new' | role object
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get('/api/customers').then(r => setCustomers(r.data.customers));
  }, []);

  const loadRoles = (customer) => {
    setSelectedCustomer(customer);
    setLoadingRoles(true);
    setRoles([]);
    setUsers([]);
    Promise.all([
      api.get(`/api/roles/customers/${customer.id}/roles`),
      api.get(`/api/customers/${customer.id}/users`),
    ]).then(([r, u]) => {
      setRoles(r.data.roles);
      setUsers(u.data.users);
      setLoadingRoles(false);
    });
  };

  const handleDelete = async (role) => {
    const affected = users.filter(u => u.customer_role_id === role.id).length;
    const msg = affected
      ? `Delete "${role.name}"? ${affected} user(s) will have their role cleared.`
      : `Delete "${role.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/api/roles/customers/${selectedCustomer.id}/roles/${role.id}`);
      toast.success('Role deleted');
      loadRoles(selectedCustomer);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      await api.patch(`/api/roles/users/${userId}/role`, { customer_role_id: roleId || null });
      toast.success('Role assigned');
      loadRoles(selectedCustomer);
    } catch (e) {
      toast.error('Failed to assign role');
    }
  };

  return (
    <>
      <div className="topbar"><h2>Role Manager</h2></div>
      <div className="page">
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>

          {/* Customer selector */}
          <div className="card" style={{ alignSelf: 'start' }}>
            <div className="card-header"><h3>Customers</h3></div>
            {customers.filter(c => c.active).map(c => (
              <div
                key={c.id}
                onClick={() => loadRoles(c)}
                style={{
                  padding: '10px 12px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                  background: selectedCustomer?.id === c.id ? 'var(--navy)' : 'transparent',
                  color: selectedCustomer?.id === c.id ? 'var(--white)' : 'var(--gray-800)',
                }}
              >
                <div className="fw-600">{c.name}</div>
                <div style={{ fontSize: 11, opacity: 0.6 }}>{c.slug}</div>
              </div>
            ))}
          </div>

          {/* Roles panel */}
          <div>
            {!selectedCustomer ? (
              <div className="card empty-state">
                <Shield size={40} />
                <p>Select a customer to manage their roles.</p>
              </div>
            ) : (
              <>
                <div className="page-header" style={{ marginBottom: 20 }}>
                  <h2 style={{ fontSize: 16 }}>{selectedCustomer.name} — Roles</h2>
                  <button className="btn btn-gold btn-sm" onClick={() => setModal('new')}>
                    <Plus size={14} /> New Role
                  </button>
                </div>

                {loadingRoles ? (
                  <div className="loading">Loading roles…</div>
                ) : roles.length === 0 ? (
                  <div className="card empty-state">
                    <p>No roles yet. Create one to get started.</p>
                  </div>
                ) : (
                  roles.map(r => (
                    <RoleCard
                      key={r.id}
                      role={r}
                      customerId={selectedCustomer.id}
                      onEdit={setModal}
                      onDelete={handleDelete}
                    />
                  ))
                )}

                {/* User role assignments */}
                {users.length > 0 && (
                  <div className="card mt-24">
                    <div className="card-header"><h3>Assign Roles to Users</h3></div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr><th>User</th><th>Email</th><th>Current Role</th><th>Assign Role</th></tr>
                        </thead>
                        <tbody>
                          {users.map(u => (
                            <tr key={u.id}>
                              <td>{u.first_name} {u.last_name}</td>
                              <td className="text-sm">{u.email}</td>
                              <td>
                                {roles.find(r => r.id === u.customer_role_id)?.name || (
                                  <span className="text-muted">None</span>
                                )}
                              </td>
                              <td>
                                <select
                                  className="form-select"
                                  style={{ width: 180 }}
                                  value={u.customer_role_id || ''}
                                  onChange={e => handleAssignRole(u.id, e.target.value ? parseInt(e.target.value) : null)}
                                >
                                  <option value="">— No Role —</option>
                                  {roles.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <RoleModal
          role={modal === 'new' ? null : modal}
          customerId={selectedCustomer?.id}
          customerSlug={selectedCustomer?.slug || ''}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadRoles(selectedCustomer); }}
        />
      )}
    </>
  );
}
